import { TypographyOptions } from "@mui/material/styles/createTypography";
import { type } from "./tokens";

/**
 * GT Brokers type scale.
 *
 * Two things changed in the redesign and both matter more than the sizes:
 *
 * 1. Headings no longer hardcode `color: "#aab8c5"`. That single pale grey was
 *    applied to h4/h5/h6 in both light and dark mode, which is most of why the
 *    old UI read as washed out. Headings now inherit, so the palette decides.
 * 2. Fluid `calc(... + vw)` sizes are gone from h1–h3. A page title that grew
 *    with the viewport made the same screen look like a different product at
 *    1366 and at 1920.
 *
 * Poppins is already loaded in index.css; nothing new is fetched.
 */

const heading = {
  fontWeight: 700,
  lineHeight: 1.2,
  letterSpacing: "-0.015em",
};

export const typography: TypographyOptions = {
  fontFamily: 'var(--bs-font-primary, "Hanken Grotesk", sans-serif)',
  h1: {
    ...heading,
    fontSize: "1.9rem",
    fontWeight: 750,
    "@media (min-width: 1200px)": {
      fontSize: "2.05rem",
    },
  },
  h2: {
    ...heading,
    fontSize: `${type.pageTitleSm}px`,
    "@media (min-width: 900px)": {
      fontSize: `${type.pageTitle}px`,
    },
  },
  h3: {
    ...heading,
    fontSize: "1.3rem",
    "@media (min-width: 1200px)": {
      fontSize: "1.4rem",
    },
  },
  h4: {
    ...heading,
    fontSize: `${type.sectionTitle}px`,
    fontWeight: 680,
  },
  h5: {
    ...heading,
    fontSize: `${type.cardTitle}px`,
    fontWeight: 660,
    letterSpacing: "-0.01em",
  },
  h6: {
    fontSize: "13px",
    fontWeight: 650,
    lineHeight: 1.35,
    letterSpacing: "-0.005em",
  },
  body1: {
    fontSize: `${type.body}px`,
    lineHeight: 1.55,
  },
  body2: {
    fontSize: "13px",
    lineHeight: 1.55,
  },
  subtitle1: {
    fontSize: `${type.body}px`,
    fontWeight: 500,
    lineHeight: 1.5,
    marginBottom: 0,
  },
  subtitle2: {
    fontSize: `${type.meta}px`,
    fontWeight: 500,
    lineHeight: 1.5,
    marginBottom: 0,
  },
  button: {
    fontSize: "13.5px",
    fontWeight: 600,
    letterSpacing: "0.005em",
    /* Enterprise software labels buttons in sentence case. The inherited
       ALL-CAPS is the loudest single tell of a stock Material template. */
    textTransform: "none",
  },
  caption: {
    fontSize: `${type.meta}px`,
    lineHeight: 1.5,
  },
  overline: {
    /* Reserved for section eyebrows and table headers. */
    fontSize: `${type.tableHeader}px`,
    fontWeight: 700,
    lineHeight: 1.6,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
  },
};
